import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import VoiceInput from '../components/VoiceInput.jsx';
import SubsegmentPicker from '../components/SubsegmentPicker.jsx';

const PAYMENT_METHODS = ['cash', 'check', 'venmo', 'zelle', 'square', 'card', 'other'];

const PRESET_ICONS = {
  eggs: '🥚',
  chicken: '🐔',
  beef_box: '🥩',
  whole_steer: '🐄',
  custom_hire: '🚜',
  usda_payment: '🇺🇸',
  other: '📦',
};

export default function IncomeNew() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({
    customer_name: '',
    date: new Date().toISOString().slice(0, 10),
    description: '',
    total_dollars: '',
    paid_dollars: '',
    payment_method: 'cash',
    chicken_batch_id: null,
    cattle_id: null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceConfidence, setVoiceConfidence] = useState(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get('/api/income/presets').then(({ presets }) => setPresets(presets)).catch(e => setErr(e.message));
  }, []);

  // Deep-link pre-fill: when navigated from a batch detail page (e.g.
  // /income/new?segment=meat_chickens&chicken_batch_id=4), auto-pick the
  // matching preset and pre-set the batch/animal so Jeff doesn't have to.
  useEffect(() => {
    if (presets.length === 0) return;
    const segment   = searchParams.get('segment');
    const sfCode    = searchParams.get('schedule_f_code');
    const batchId   = searchParams.get('chicken_batch_id');
    const cattleId  = searchParams.get('cattle_id');
    if (segment) {
      const match = (sfCode && presets.find(p => p.segment_id === segment && p.schedule_f_code === sfCode))
                 || presets.find(p => p.segment_id === segment);
      if (match) pick(match);
    }
    if (batchId)  setForm(f => ({ ...f, chicken_batch_id: Number(batchId), cattle_id: null }));
    if (cattleId) setForm(f => ({ ...f, cattle_id: Number(cattleId), chicken_batch_id: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets]);

  function pick(p) {
    setChosen(p);
    setForm(f => ({ ...f, description: f.description || `1 ${p.default_unit}` }));
  }

  // Voice fast-path: send transcript → server parses → we pick the matching
  // preset by segment_id+schedule_f_code and pre-fill the form. The owner still
  // reviews + clicks Save.
  async function onVoiceTranscript(transcript) {
    setVoiceBusy(true); setErr(''); setVoiceConfidence(null);
    try {
      const { parsed } = await api.post('/api/voice/income', { transcript });
      const match = presets.find(p =>
        p.segment_id === parsed.segment_id && p.schedule_f_code === parsed.schedule_f_code,
      ) || presets.find(p => p.segment_id === parsed.segment_id);
      if (match) setChosen(match);
      setForm(f => ({
        ...f,
        customer_name: parsed.customer_name ?? f.customer_name,
        description:   parsed.description   ?? f.description,
        total_dollars: parsed.total_cents != null ? (parsed.total_cents / 100).toFixed(2) : f.total_dollars,
        paid_dollars:  parsed.paid_cents  != null ? (parsed.paid_cents  / 100).toFixed(2) : f.paid_dollars,
        payment_method: parsed.payment_method || f.payment_method,
      }));
      setVoiceConfidence(parsed._confidence ?? null);
    } catch (e) {
      setErr(`Voice parse failed: ${e.message}`);
    } finally {
      setVoiceBusy(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!chosen) return;
    setBusy(true); setErr('');
    try {
      const total_cents = Math.round(Number(form.total_dollars || 0) * 100);
      const paid_cents = form.paid_dollars
        ? Math.round(Number(form.paid_dollars) * 100)
        : total_cents;
      if (!total_cents) throw new Error('total required');
      const body = {
        customer_name: form.customer_name.trim() || null,
        date: form.date,
        description: form.description.trim() || null,
        total_cents,
        paid_cents,
        payment_method: form.payment_method,
        segment_id: chosen.segment_id,
        schedule_f_code: chosen.schedule_f_code,
        chicken_batch_id: form.chicken_batch_id,
        cattle_id: form.cattle_id,
      };
      await api.post('/api/income', body);
      navigate('/income');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="text-2xl font-serif text-olive-900">Log Income</h1>

      {!chosen ? (
        <>
          <div className="card flex flex-wrap items-center gap-3 bg-olive-50">
            <VoiceInput onTranscript={onVoiceTranscript} busy={voiceBusy}
                        prompt='🎤 Voice entry' />
            <p className="text-xs text-ink-700/70 flex-1 min-w-[12rem]">
              Try: "<em>Sold three dozen eggs to Amy for twenty bucks cash</em>" — the form auto-fills, you confirm and save.
            </p>
          </div>
          {voiceBusy && <p className="text-xs text-ink-700/60 italic">Parsing your utterance…</p>}
          {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

          <p className="text-sm text-ink-700/70">…or pick a category — segment + Schedule F line auto-fill.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {presets.map(p => (
              <button
                key={p.id}
                onClick={() => pick(p)}
                className="card hover:bg-olive-50 transition flex flex-col items-center text-center py-6"
              >
                <div className="text-3xl mb-2">{PRESET_ICONS[p.id] || '📦'}</div>
                <div className="font-medium text-olive-900">{p.label}</div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="card flex items-center gap-3">
            <span className="text-2xl">{PRESET_ICONS[chosen.id]}</span>
            <div className="flex-1">
              <div className="font-medium">{chosen.label}</div>
              <div className="text-xs text-ink-700/60">Schedule F line · {chosen.schedule_f_code}</div>
            </div>
            {voiceConfidence != null && (
              <span className="pill bg-gold-500/15 text-gold-600" title="From voice">
                🎤 conf {(voiceConfidence * 100).toFixed(0)}%
              </span>
            )}
            <button type="button" onClick={() => setChosen(null)} className="text-sm text-olive-700 hover:underline">Change</button>
          </div>

          <div className="card grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="label">Customer name</label>
              <input className="input" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="Amy" />
            </div>
            <div className="col-span-2">
              <label className="label">Description</label>
              <input className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={`1 ${chosen.default_unit}`} />
            </div>
            <SubsegmentPicker
              segmentId={chosen.segment_id}
              value={form.chicken_batch_id ?? form.cattle_id}
              onChange={(patch) => setForm(f => ({ ...f, chicken_batch_id: null, cattle_id: null, ...patch }))}
            />
            <div>
              <label className="label">Total $ *</label>
              <input className="input" type="number" step="0.01" required value={form.total_dollars} onChange={e => setForm(f => ({ ...f, total_dollars: e.target.value }))} />
            </div>
            <div>
              <label className="label">Paid $ (blank = paid in full)</label>
              <input className="input" type="number" step="0.01" value={form.paid_dollars} onChange={e => setForm(f => ({ ...f, paid_dollars: e.target.value }))} />
            </div>
            <div>
              <label className="label">Date</label>
              <input className="input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Payment</label>
              <select className="input" value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
                {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

          <div className="flex gap-2">
            <button type="button" onClick={() => navigate('/income')} className="btn-ghost">Cancel</button>
            <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      )}
    </div>
  );
}
